<template>
  <div class="flex flex-col gap-y-1" :class="[fullWidth && '!w-full']">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
      <label v-if="label" :class="labelClass" class="flex items-center gap-x-2">
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div v-for="(item, key) in selectOptions" class="gap-2 flex flex-wrap">
        <button
          :key="key"
          :disabled="isDisabled"
          class="duration-0 flex justify-center items-center border-2 rounded-md text-black bg-gray-100/80 hover:bg-primary hover:text-white disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          :class="[inputValue === item && '!bg-primary !text-white']"
          @click.prevent="onClick(item)"
        >
          <span
            class="duration-0 flex justify-center items-center p-2 pe-10 ltr:font-sans tracking-wide"
            :class="[inputValue === item ? 'ps-4' : 'ps-10']"
          >
            <div v-show="inputValue === item" class="fa icon-check px-1"></div>
            {{ formateMobile(item) }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import useVisible from '@/composables/useVisible'
import useMobileButtonListActions from '@/composables/actions/useMobileButtonListActions'

import { onMounted, ref, computed, watchEffect,watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useFormStore } from '@/stores/form'
import { useLookupsStore } from '@/stores/lookups'
import { useField } from 'vee-validate'
import useRuleHandler from '@/composables/useRuleHandler'
import lodashGet from 'lodash/get'

const props = defineProps({
  // general props

  rules: Array,
  hint: String,
  fieldName: String,
  resetValue: Object,
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  icon: {
    type: String,
    required: ''
  },
  text: {
    type: String,
    required: ''
  },
  options: {
    type: Array,
    required: true
  },
  label: {
    type: String
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  trigger: String,
  on_click: Object,
  defaultValue: String,
  disabled: [Boolean, Object]
})

const route = useRoute()
const i18n = useI18n()
const formStore = useFormStore()
const lookupStore = useLookupsStore()
const { findShowRule, getBindObj } = useVisible()

const {
  value: inputValue,
  handleBlur: handleFieldBlur,
  handleChange
} = useField(props.fieldName, (value) => useRuleHandler(value, props.rules), {
  initialValue: props.defaultValue
})

const selectOptions = ref()

const t9nLabel = computed(() => i18n.t(props.label || ''))
const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))

watchEffect(() => {
  getBindObj(route.params.serviceId)
})

const isDisabled = computed(() => {
  if (typeof props.disabled === 'boolean') {
    return props.disabled
  }

  if (Object.keys(props.disabled).length > 0) {
    const result = findShowRule(
      props.disabled.any || props.disabled.all,
      props.disabled.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
    return result
  }
  return false
})

const formateMobile = (mobileNumber) => {
  const currentMobile = mobileNumber.replace('00971', '0')
  return `${currentMobile.substring(0, 3)}****${currentMobile.substring(currentMobile.length - 3)}`
}

const onClick = async (number) => {
  !isDisabled.value && handleChange(number)
  if (props.trigger == 'click') {
    if (props.on_click.action) {
      await useMobileButtonListActions()[props.on_click.action](number)
    }
  }
  if (props.resetValue) {
    formStore.form.setFieldValue(`${props.resetValue.ref}`, number)
  }
}

// Process value from dynamic fields, then pass to query of the api.
const processQuery = (query) => {
  if (!query) return
  const newQuery = {}
  if (Object.keys(query).length > 0) {
    Object.keys(query).map((key) => {
      if (query[key].ref) {
        newQuery[key] = formStore.extraMap[query[key].ref] || formStore.form.values[query[key].ref]
      } else {
        newQuery[key] = query[key]
      }
    })
  }
  return newQuery
}
// Process value from dynamic fields, then replace the patch with these.
const processPathQuery = (queryPath) => {
  if (!queryPath) return
  let newQueryPath = ''
  if (Object.keys(queryPath).length > 0) {
    Object.keys(queryPath).map((key) => {
      if (queryPath[key].ref) {
        const value =
          lodashGet(formStore.extraMap, queryPath[key].ref) ||
          lodashGet(formStore.form.values, queryPath[key].ref)
        newQueryPath += `/${value}`
      } else {
        newQueryPath += queryPath[key]
      }
    })
  }
  return newQueryPath
}

const selecteWhenOnlyOne = () => {
  if(selectOptions.value && selectOptions.value.length == 1) {
    onClick(selectOptions.value[0])
  }
}

watch(
  () => isDisabled.value,
  (newValue) => {
    if(!newValue) {
      selecteWhenOnlyOne();
    }
  }
)

onMounted(async () => {
  // otherwise call the api
  const { path, query, body } = props.options.api
  let newPath = path
  if (formStore.form.values[props.dependentOn] && newPath.includes('__reactive__')) {
    newPath = newPath.replaceAll('__reactive__', formStore.form.values[props.dependentOn])
  }

  let options = []
  if (props.options.api.path_query) {
    newPath += processPathQuery(props.options.api.path_query)
  }
  const newQuery = processQuery(query)
  const newData = processQuery(body)
  try {
    options = await lookupStore.fetchLookup(
      newPath,
      newQuery,
      {},
      props.options.api.needProcess,
      props.options.api.method,
      newData,
      props.options.api.need_cache
    )
  } catch (error) {
    console.error('fetchLookup MobileButton', error)
  }
  if (props.options.api.on_success?.action) {
    const { action, action_params } = props.options.api.on_success
    options = await useMobileButtonListActions()[action](options, action_params)
  }

  selectOptions.value = options;

  await nextTick(); 
  selecteWhenOnlyOne();
  
})
</script>

<style lang="scss" scoped></style>
